import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Images
import stepsImage from '../../assets/images/homeowner-steps-diagram.png';
import hvacContractorIcon from '../../assets/images/hvac-contractor-icon.png';
import techHoldingHaven from '../../assets/images/tech-holding-haven.png';
import downloadAppStore from '../../assets/images/apple-app-store-button.jpg';
import downloadGooglePlay from '../../assets/images/google-play-button.jpg';
import maskGroup5 from '../../assets/images/mask-group-5.jpg';
import group567 from '../../assets/images/group-567-1-1.jpg';
import maskGroup6 from '../../assets/images/mask-group-6.jpg';
import group568 from '../../assets/images/group-568.jpg';

// Styled components
const HeroSection = styled(Box)(({ theme }) => ({
  minHeight: '70vh',
  display: 'flex',
  alignItems: 'center',
  background: 'linear-gradient(135deg, #00C4C1 0%, #FFE623 100%)',
  color: 'white',
  position: 'relative',
  overflow: 'hidden',
}));



const OptionCard = styled(Card)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(4),
  height: '100%',
  border: `2px solid ${theme.palette.grey[200]}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[8],
  },
}));

const OrDivider = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: theme.spacing(4, 0),
  '&::before, &::after': {
    content: '""',
    flex: 1,
    height: '1px',
    backgroundColor: theme.palette.grey[300],
  },
  '&::before': {
    marginRight: theme.spacing(2),
  },
  '&::after': {
    marginLeft: theme.spacing(2),
  },
}));

const HomeownerStartPage: React.FC = () => {
  const theme = useTheme();

  return (
    <Box>
      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="xl">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h2" component="h1" sx={{ 
                fontWeight: 400, 
                mb: 3,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                lineHeight: 1.4,
                pl: { xs: 5, md: 20 }
              }}>
                You're on your way to professionally managed air.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: 'center' }}>
                <img 
                  src={stepsImage} 
                  alt="HAVEN Installation Steps" 
                  style={{ 
                    width: '100%', 
                    maxWidth: '450px', 
                    height: 'auto'
                  }} 
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </HeroSection>

      {/* Step 1 Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h4" sx={{ 
            color: theme.palette.primary.main,
            fontWeight: 'bold',
            mb: 2
          }}>
            Step 1
          </Typography>
          <Typography variant="h3" component="h2" sx={{ 
            fontWeight: 'bold',
            color: '#455a64',
            mb: 4
          }}>
            Ready to get started?
          </Typography>
        </Box>

        <Grid container spacing={4} alignItems="center">
          {/* Ask HVAC Contractor Option */}
          <Grid item xs={12} md={5}>
            <OptionCard>
              <CardContent>
                <Box sx={{ mb: 3 }}>
                  <img 
                    src={hvacContractorIcon} 
                    alt="HVAC Contractor" 
                    style={{ 
                      width: '120px', 
                      height: 'auto',
                      marginBottom: '16px'
                    }} 
                  />
                </Box>
                <Typography variant="h5" component="h3" sx={{ 
                  fontWeight: 'bold',
                  mb: 3,
                  color: '#455a64'
                }}>
                  Ask your HVAC Contractor
                </Typography>
                <Typography variant="body1" sx={{ 
                  color: '#666',
                  lineHeight: 1.6,
                  mb: 3
                }}>
                  Have you talked to your trusted HVAC service company? They might be installing HAVEN in your area already!
                </Typography>
                <Button
                  variant="outlined"
                  size="large"
                  href="mailto:support@haveniaq.com"
                  sx={{
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    fontWeight: 'bold',
                    px: 4,
                    py: 1.5,
                    '&:hover': {
                      borderColor: theme.palette.primary.dark,
                      backgroundColor: theme.palette.primary.main,
                      color: 'white',
                    },
                  }}
                >
                  GET IN TOUCH WITH HAVEN
                </Button>
              </CardContent>
            </OptionCard>
          </Grid>

          {/* OR Divider */}
          <Grid item xs={12} md={2}>
            <OrDivider>
              <Typography variant="h6" sx={{ 
                backgroundColor: 'white',
                padding: '8px 16px',
                borderRadius: '50%',
                border: `2px solid ${theme.palette.primary.main}`,
                color: theme.palette.primary.main,
                fontWeight: 'bold'
              }}>
                OR
              </Typography>
            </OrDivider>
          </Grid>

          {/* Purchase through HAVEN Option */}
          <Grid item xs={12} md={5}>
            <OptionCard>
              <CardContent>
                <Box sx={{ mb: 3 }}>
                  <img 
                    src={techHoldingHaven} 
                    alt="Purchase through HAVEN" 
                    style={{ 
                      width: '120px', 
                      height: 'auto',
                      marginBottom: '16px'
                    }} 
                  />
                </Box>
                <Typography variant="h5" component="h3" sx={{ 
                  fontWeight: 'bold',
                  mb: 3,
                  color: '#455a64'
                }}>
                  Purchase through HAVEN
                </Typography>
                <Typography variant="body1" sx={{ 
                  color: '#666',
                  lineHeight: 1.6,
                  mb: 3
                }}>
                  Don't have a preferred HVAC company yet? Purchase through HAVEN, and we'll connect you with a company in your area to perform the install.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  href="https://buy.stripe.com/14keWldBvfND7BudQZ"
                  target="_blank"
                  sx={{
                    background: theme.palette.primary.main,
                    color: 'white',
                    fontWeight: 'bold',
                    px: 4,
                    py: 1.5,
                    '&:hover': {
                      background: theme.palette.primary.dark,
                    },
                  }}
                >
                  BUY NOW
                </Button>
              </CardContent>
            </OptionCard>
          </Grid>
        </Grid>
      </Container>

      {/* Step 2 Section */}
      <Box sx={{ backgroundColor: '#f8f9fa', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h4" sx={{ 
              color: theme.palette.primary.main,
              fontWeight: 'bold',
              mb: 2
            }}>
              Step 2
            </Typography>
            <Typography variant="h3" component="h2" sx={{ 
              fontWeight: 'bold',
              color: '#455a64',
              mb: 4
            }}>
              After HAVEN Installation
            </Typography>
          </Box>

          <Grid container spacing={4} alignItems="center">
            {/* Left side - Woman with phone image */}
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: 'center' }}>
                <img 
                  src={maskGroup5} 
                  alt="Woman using HAVEN app" 
                  style={{ 
                    width: '100%', 
                    maxWidth: '500px', 
                    height: 'auto',
                    borderRadius: '12px'
                  }} 
                />
              </Box>
            </Grid>

            {/* Right side - App download section */}
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h5" sx={{ 
                  color: theme.palette.primary.main,
                  fontWeight: 'bold',
                  mb: 3
                }}>
                  Download the HAVEN IAQ App
                </Typography>
                
                {/* Device diagram */}
                <Box sx={{ mb: 4 }}>
                  <img 
                    src={group567} 
                    alt="HAVEN devices and app connection" 
                    style={{ 
                      width: '100%', 
                      maxWidth: '400px', 
                      height: 'auto'
                    }} 
                  />
                </Box>

                <Typography variant="body1" sx={{ 
                  color: '#666',
                  lineHeight: 1.6,
                  mb: 4,
                  maxWidth: '400px',
                  mx: 'auto'
                }}>
                  Get your HAVEN devices installed and download the HAVEN IAQ app. Manage automations and receive real-time air quality alerts.
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <a href="https://haveniaq.com/android-app" target="_blank" rel="noopener noreferrer">
                    <img 
                      src={downloadGooglePlay} 
                      alt="Get it on Google Play" 
                      style={{ height: '60px', cursor: 'pointer' }} 
                    />
                  </a>
                  <a href="https://haveniaq.com/ios-app" target="_blank" rel="noopener noreferrer">
                    <img 
                      src={downloadAppStore} 
                      alt="Download on App Store" 
                      style={{ height: '60px', cursor: 'pointer' }} 
                    />
                  </a>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Step 3 Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={4} alignItems="center">
          {/* Left side - Woman with gradient image */}
          <Grid item xs={12} md={6}>
            <Box sx={{ 
              textAlign: 'center',
              position: 'relative',
              display: 'inline-block',
              maxWidth: '500px',
              mx: 'auto'
            }}>
              <img 
                src={maskGroup6} 
                alt="Woman enjoying clean air" 
                style={{ 
                  width: '100%', 
                  height: 'auto',
                  borderRadius: '12px',
                  display: 'block'
                }} 
              />
              {/* Gradient overlay */}
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(240deg, #00C4C1 0%, #FFE623 100%)',
                borderRadius: '12px',
                opacity: 0.7,
                mixBlendMode: 'multiply',
                pointerEvents: 'none'
              }} />
            </Box>
          </Grid>

          {/* Right side - Content section */}
          <Grid item xs={12} md={6}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ 
                color: theme.palette.primary.main,
                fontWeight: 'bold',
                mb: 2
              }}>
                Step 3
              </Typography>
              <Typography variant="h3" component="h2" sx={{ 
                fontWeight: 'bold',
                color: '#455a64',
                mb: 3
              }}>
                Enjoy the benefits of Active Air Management
              </Typography>
              
              {/* Device diagram */}
              <Box sx={{ mb: 4 }}>
                <img 
                  src={group568} 
                  alt="HAVEN devices with checkmarks" 
                  style={{ 
                    width: '100%', 
                    maxWidth: '300px', 
                    height: 'auto'
                  }} 
                />
              </Box>

              <Typography variant="h5" sx={{ 
                color: '#455a64',
                fontWeight: 'bold',
                mb: 3
              }}>
                Activate your air with HAVEN
              </Typography>

              <Typography variant="body1" sx={{ 
                color: '#666',
                lineHeight: 1.6,
                mb: 2,
                maxWidth: '400px',
                mx: 'auto'
              }}>
                Learn more about your HAVEN Score and how to take your air quality to the next level!
              </Typography>

              <Typography variant="body1" sx={{ 
                color: '#666',
                lineHeight: 1.6,
                mb: 4,
                maxWidth: '400px',
                mx: 'auto'
              }}>
                Read more about indoor air quality (IAQ) and the three pillars that improve it!
              </Typography>

              <Button
                variant="contained"
                size="large"
                href="https://haveniaq.com/news/"
                target="_blank"
                sx={{
                  background: theme.palette.primary.main,
                  color: 'white',
                  fontWeight: 'bold',
                  px: 4,
                  py: 1.5,
                  '&:hover': {
                    background: theme.palette.primary.dark,
                  },
                }}
              >
                READ MORE ABOUT IAQ
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default HomeownerStartPage; 